// src/algorithms/teamRebalancing.ts
import { Person, Project } from '../types';
import { mockPeople, mockProjects } from '../data/mockData';

export interface TeamRebalancingInput {
  personId: string;
  fromProjectId: string;
  toProjectId: string;
  transitionTimeline: 'immediate' | 'phased' | 'delayed';
}

export interface TeamRebalancingOutput {
  riskAssessment: {
    overallRiskScore: number;
    primaryRisks: Array<{
      category: 'skills' | 'dependencies' | 'timeline' | 'morale';
      description: string;
      impactScore: number;
      mitigation: string;
    }>;
    timelineImpact: {
      sourceProjectDelayWeeks: number;
      targetProjectAccelerationWeeks: number;
      netOrganizationImpact: number;
    };
  };
  recommendations: Array<{
    priority: 'critical' | 'high' | 'medium' | 'low';
    action: string;
    responsible: string;
    timeline: string;
    expectedOutcome: string;
  }>;
  alternativeOptions: Array<{
    personId: string;
    name: string;
    matchScore: number;
    pros: string[];
    cons: string[];
  }>;
  visualizationData: {
    skillGaps: Array<{ skill: string; required: number; current: number; gap: number }>;
    timelineComparison: Array<{ week: number; withMove: number; withoutMove: number }>;
    dependencyImpact: Array<{ project: string; impact: 'high' | 'medium' | 'low' }>;
  };
}

export function analyzeTeamRebalancing(
  input: TeamRebalancingInput
): TeamRebalancingOutput {
  const person = mockPeople.find(p => p.id === input.personId);
  const sourceProject = mockProjects.find(p => p.id === input.fromProjectId);
  const targetProject = mockProjects.find(p => p.id === input.toProjectId);

  if (!person || !sourceProject || !targetProject) {
    throw new Error('Invalid input data');
  }

  // 1. Calculate skill gaps
  const skillGaps = targetProject.requiredSkills.map(reqSkill => {
    const personSkill = person.skillLevels[reqSkill.skill] || 0;
    return {
      skill: reqSkill.skill,
      required: reqSkill.minLevel,
      current: personSkill,
      gap: Math.max(0, reqSkill.minLevel - personSkill),
    };
  });

  // 2. Calculate dependency impact
  const dependentProjects = mockProjects.filter(p => 
    p.dependencies.includes(input.fromProjectId)
  );

  // 3. Calculate timeline impacts (intelligence algorithm)
  const sourceProjectDelay = calculateCriticalPathDelay(person, sourceProject);
  const targetProjectAcceleration = calculateAcceleration(person, targetProject, skillGaps);

  // 4. Calculate overall risk score
  const overallRiskScore = calculateOverallRisk(
    skillGaps,
    dependentProjects.length,
    sourceProjectDelay,
    person.isCriticalPath
  );

  // 5. Generate recommendations
  const recommendations = generateRecommendations(
    skillGaps,
    sourceProjectDelay,
    dependentProjects,
    input.transitionTimeline
  );

  // 6. Find alternative candidates
  const alternatives = findAlternativeCandidates(targetProject, person.id);

  // 7. Generate timeline data for visualization
  const timelineComparison = generateTimelineData(sourceProjectDelay, targetProjectAcceleration);

  return {
    riskAssessment: {
      overallRiskScore,
      primaryRisks: identifyPrimaryRisks(skillGaps, dependentProjects, sourceProjectDelay),
      timelineImpact: {
        sourceProjectDelayWeeks: sourceProjectDelay,
        targetProjectAccelerationWeeks: targetProjectAcceleration,
        netOrganizationImpact: sourceProjectDelay - targetProjectAcceleration,
      },
    },
    recommendations,
    alternativeOptions: alternatives,
    visualizationData: {
      skillGaps,
      timelineComparison,
      dependencyImpact: dependentProjects.map(p => ({
        project: p.name,
        impact: calculateDependencyImpact(p, person),
      })),
    },
  };
}

// Helper functions with intelligence logic
function calculateCriticalPathDelay(person: Person, project: Project): number {
  if (!person.isCriticalPath) return 2;
  
  const baseDelay = 4;
  const workloadFactor = person.workload / 100;
  const productivityFactor = (100 - person.productivityScore) / 100;
  const tenureFactor = 12 / person.tenureMonths;
  
  return Math.round(baseDelay * workloadFactor * productivityFactor * tenureFactor);
}

function calculateAcceleration(
  person: Person, 
  project: Project, 
  skillGaps: Array<{ gap: number }>
): number {
  const baseAcceleration = 3;
  const skillMatch = 1 - (skillGaps.reduce((sum, gap) => sum + gap.gap, 0) / (skillGaps.length * 10));
  const productivityFactor = person.productivityScore / 100;
  
  return Math.round(baseAcceleration * skillMatch * productivityFactor);
}

function calculateOverallRisk(
  skillGaps: Array<{ gap: number }>,
  dependentCount: number,
  delayWeeks: number,
  isCriticalPath: boolean
): number {
  let risk = 0;
  
  // Skill gap risk (max 40 points)
  const avgSkillGap = skillGaps.reduce((sum, gap) => sum + gap.gap, 0) / skillGaps.length;
  risk += Math.min(40, avgSkillGap * 10);
  
  // Dependency risk (max 30 points)
  risk += Math.min(30, dependentCount * 10);
  
  // Timeline risk (max 20 points)
  risk += Math.min(20, delayWeeks * 5);
  
  // Critical path risk (max 10 points)
  risk += isCriticalPath ? 10 : 0;
  
  return Math.min(100, risk);
}

function generateRecommendations(
  skillGaps: Array<{ skill: string; gap: number }>,
  delayWeeks: number,
  dependentProjects: Project[],
  timeline: string
): TeamRebalancingOutput['recommendations'] {
  const recommendations: TeamRebalancingOutput['recommendations'] = [];
  
  // Skill gap recommendations
  const criticalGaps = skillGaps.filter(gap => gap.gap >= 3);
  if (criticalGaps.length > 0) {
    recommendations.push({
      priority: 'critical',
      action: `Provide training in ${criticalGaps.map(g => g.skill).join(', ')} before transition`,
      responsible: 'Learning & Development',
      timeline: '2-4 weeks',
      expectedOutcome: 'Reduce skill gap by 70%',
    });
  }
  
  // Dependency recommendations
  if (dependentProjects.length > 0) {
    recommendations.push({
      priority: 'high',
      action: `Create backup plan for ${dependentProjects.length} dependent projects`,
      responsible: 'Project Manager',
      timeline: '1 week',
      expectedOutcome: 'Minimize disruption to dependent projects',
    });
  }
  
  // Timeline recommendations
  if (delayWeeks > 4) {
    recommendations.push({
      priority: 'high',
      action: 'Consider phased transition over 2-3 weeks',
      responsible: 'Department Head',
      timeline: 'Immediate',
      expectedOutcome: 'Reduce immediate impact by 50%',
    });
  }
  
  // Always include knowledge transfer recommendation
  recommendations.push({
    priority: 'medium',
    action: 'Schedule knowledge transfer sessions',
    responsible: 'Team Lead',
    timeline: 'Before transition',
    expectedOutcome: 'Ensure smooth handover of responsibilities',
  });
  
  return recommendations;
}

function findAlternativeCandidates(targetProject: Project, excludedPersonId: string) {
  return mockPeople
    .filter(p => p.id !== excludedPersonId && p.workload < 80)
    .map(person => {
      const matchScore = calculatePersonProjectMatch(person, targetProject);
      return {
        personId: person.id,
        name: person.name,
        matchScore,
        pros: generatePros(person, targetProject),
        cons: generateCons(person, targetProject),
      };
    })
    .sort((a, b) => b.matchScore - a.matchScore)
    .slice(0, 3);
}

function calculatePersonProjectMatch(person: Person, project: Project): number {
  let match = 0;
  let totalWeight = 0;
  
  project.requiredSkills.forEach(reqSkill => {
    const weight = reqSkill.importance === 'critical' ? 3 : 
                   reqSkill.importance === 'high' ? 2 : 1;
    const personSkill = person.skillLevels[reqSkill.skill] || 0;
    const skillMatch = Math.min(100, (personSkill / reqSkill.minLevel) * 100);
    
    match += skillMatch * weight;
    totalWeight += weight;
  });
  
  return totalWeight > 0 ? Math.round(match / totalWeight) : 0;
}

function generatePros(person: Person, project: Project): string[] {
  const pros: string[] = [];
  
  if (person.workload < 70) pros.push('Available capacity');
  if (person.productivityScore > 85) pros.push('High performer');
  if (person.tenureMonths > 24) pros.push('Company knowledge');
  
  // Check for matching career interests
  const projectTechs = project.requiredSkills.map(s => s.skill);
  const matchingInterests = person.careerInterests.filter(interest =>
    projectTechs.some(tech => interest.toLowerCase().includes(tech.toLowerCase()))
  );
  if (matchingInterests.length > 0) {
    pros.push('Career alignment');
  }
  
  return pros;
}

function generateCons(person: Person, project: Project): string[] {
  const cons: string[] = [];
  
  if (person.isCriticalPath) cons.push('Critical to current project');
  if (person.workload > 85) cons.push('High current workload');
  
  // Check skill gaps
  const significantGaps = project.requiredSkills.filter(reqSkill => {
    const personSkill = person.skillLevels[reqSkill.skill] || 0;
    return personSkill < reqSkill.minLevel - 2;
  });
  
  if (significantGaps.length > 0) {
    cons.push(`Skill gaps: ${significantGaps.map(g => g.skill).join(', ')}`);
  }
  
  return cons;
}

function identifyPrimaryRisks(
  skillGaps: Array<{ skill: string; gap: number }>,
  dependentProjects: Project[],
  delayWeeks: number
): TeamRebalancingOutput['riskAssessment']['primaryRisks'] {
  const risks: TeamRebalancingOutput['riskAssessment']['primaryRisks'] = [];
  
  // Skill gap risks
  const criticalSkillGaps = skillGaps.filter(gap => gap.gap >= 3);
  if (criticalSkillGaps.length > 0) {
    risks.push({
      category: 'skills',
      description: `Critical skill gaps in ${criticalSkillGaps.map(g => g.skill).join(', ')}`,
      impactScore: 75,
      mitigation: 'Immediate training or pair programming',
    });
  }
  
  // Dependency risks
  if (dependentProjects.length > 0) {
    risks.push({
      category: 'dependencies',
      description: `${dependentProjects.length} projects depend on this resource`,
      impactScore: dependentProjects.length * 20,
      mitigation: 'Create backup resources and contingency plans',
    });
  }
  
  // Timeline risks
  if (delayWeeks > 3) {
    risks.push({
      category: 'timeline',
      description: `Project delay of ${delayWeeks} weeks expected`,
      impactScore: delayWeeks * 15,
      mitigation: 'Phased transition or additional temporary resources',
    });
  }
  
  return risks;
}

function generateTimelineData(delayWeeks: number, accelerationWeeks: number) {
  const data = [];
  for (let week = 1; week <= 8; week++) {
    data.push({
      week,
      withMove: Math.max(0, 100 - (week * 12.5) + (week > 2 ? delayWeeks * 5 : 0)),
      withoutMove: 100 - (week * 12.5),
    });
  }
  return data;
}

function calculateDependencyImpact(project: Project, person: Person): 'high' | 'medium' | 'low' {
  if (person.isCriticalPath) return 'high';
  if (project.status === 'red') return 'high';
  if (project.velocity < 50) return 'medium';
  return 'low';
}
